'use client'
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import OneSignal from 'react-onesignal'

export enum NotificationStatus {
  NoSupport = 'no-support',
  Enabled = 'enabled',
  Denied = 'denied',
  Disabled = 'disabled',
  Prompt = 'prompt',
}

const getNotificationStatus = (permission?: NotificationPermission) => {
  if (typeof window === 'object' && !('Notification' in window)) {
    return NotificationStatus.NoSupport
  }

  if (typeof Notification !== 'undefined') {
    const permission_ = permission || Notification.permission
    if (permission_ === 'granted') {
      return NotificationStatus.Enabled
    }
    if (permission_ === 'denied') {
      return NotificationStatus.Denied
    }
    if (permission_ === 'default') {
      return NotificationStatus.Prompt
    }
  }

  return NotificationStatus.Disabled
}

const askForNotificationPermission = async () => {
  try {
    await OneSignal.Notifications.requestPermission()
    return getNotificationStatus()
  } catch (e) {
    return NotificationStatus.Disabled
  }
}

export const NotificationContext = createContext({
  initialized: false,
  status: NotificationStatus.Prompt,
  enable: async () => NotificationStatus.Disabled,
  disable: () => {},
  logIn: async (_id: string, _email: string) => {},
  logOut: async () => {},
  setPushSubscription: async (_enabled: boolean) => {},
  isPushEnabled: false,
})

type NotificaitonProviderProps = PropsWithChildren<{ appId: string }>

export const NotificationProvider = ({
  appId,
  children,
}: NotificaitonProviderProps) => {
  const [isInitialized, setInitialized] = useState(false)
  const [status, setStatus] = useState<NotificationStatus>(
    getNotificationStatus(),
  )
  const isMountedRef = useRef(false)
  const [isPushEnabled, setIsPushEnabled] = useState(false)

  const initialize = useCallback(async () => {
    await OneSignal.init({
      appId,
      allowLocalhostAsSecureOrigin: true,
      promptOptions: {
        autoPrompt: false,
      },
      serviceWorkerParam: {
        scope: '/push/onesignal',
      },
      welcomeNotification: {
        disable: true,
      },
    })
    setInitialized(true)
  }, [appId])

  useEffect(() => {
    if (isMountedRef.current) {
      return
    }

    isMountedRef.current = true
    initialize()
  }, [initialize, status])

  const enable = useCallback(async () => {
    if (!isInitialized) {
      return status
    }
    if (status === NotificationStatus.Enabled) {
      return status
    }

    const newStatus = await askForNotificationPermission()
    setStatus(newStatus)

    return newStatus
  }, [status, isInitialized])

  const disable = useCallback(() => {
    setStatus(NotificationStatus.Disabled)
    setInitialized(false)
  }, [])

  const logIn = useCallback(
    async (userId: string, email: string) => {
      if (isInitialized) {
        await OneSignal.login(userId)
        OneSignal.User.addEmail(email)
      }
    },
    [isInitialized],
  )

  const logOut = useCallback(async () => {
    if (isInitialized) {
      await OneSignal.logout()
    }
  }, [isInitialized])

  const setPushSubscription = useCallback(
    async (enabled: boolean) => {
      if (!isInitialized) return

      try {
        if (enabled) {
          await OneSignal.User.PushSubscription.optIn()
          setIsPushEnabled(true)
          console.log('✅ Successfully subscribed to push notifications')
        } else {
          await OneSignal.User.PushSubscription.optOut()
          setIsPushEnabled(false)
          console.log('✅ Successfully unsubscribed from push notifications')
        }
      } catch (error) {
        console.error('❌ Error updating push subscription:', error)
      }
    },
    [isInitialized],
  )

  return (
    <NotificationContext.Provider
      value={{
        initialized: isInitialized,
        status,
        enable,
        disable,
        logIn,
        logOut,
        setPushSubscription,
        isPushEnabled,
      }}
    >
      {children}
    </NotificationContext.Provider>
  )
}

export const useNotification = () => {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error(
      'useNotification must be used within a NotificationProvider',
    )
  }
  return context
}
